import { supabase } from "@/infrastructure/supabase/client";
import type {
  DecisionRow,
  EntityType,
  FeatureRow,
  MeetingAttendeeRow,
  MeetingLinkRow,
  MeetingRow,
  OpenQuestionRow,
  PainPointRow,
  RiskRow,
  StakeholderRow,
  TaskRow,
} from "@/infrastructure/supabase/database.types";
import { normalizeFeatureRow } from "@/infrastructure/repositories/features.supabase.repository";

export interface MeetingFilters {
  phaseId?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
}

export interface MeetingDetail extends MeetingRow {
  attendees: MeetingAttendeeRow[];
  links: MeetingLinkRow[];
}

export interface MeetingFullDetail extends MeetingDetail {
  attendeeStakeholders: StakeholderRow[];
  decisions: DecisionRow[];
  risks: RiskRow[];
  openQuestions: OpenQuestionRow[];
  tasks: TaskRow[];
  features: FeatureRow[];
  painPoints: PainPointRow[];
}

async function expandLinks(
  meetingId: string,
  links: MeetingLinkRow[]
): Promise<{
  decisions: DecisionRow[];
  risks: RiskRow[];
  openQuestions: OpenQuestionRow[];
  tasks: TaskRow[];
  features: FeatureRow[];
  painPoints: PainPointRow[];
}> {
  void meetingId;
  const idsByType = links.reduce<Record<EntityType, string[]>>(
    (acc, link) => {
      acc[link.entity_type].push(link.entity_id);
      return acc;
    },
    {
      decision: [],
      risk: [],
      open_question: [],
      task: [],
      feature: [],
      pain_point: [],
    }
  );

  const [decisions, risks, openQuestions, tasks, features, painPoints] = await Promise.all([
    idsByType.decision.length
      ? supabase.from("decisions").select("*").in("id", idsByType.decision)
      : Promise.resolve({ data: [] as DecisionRow[], error: null }),
    idsByType.risk.length
      ? supabase.from("risks").select("*").in("id", idsByType.risk)
      : Promise.resolve({ data: [] as RiskRow[], error: null }),
    idsByType.open_question.length
      ? supabase.from("open_questions").select("*").in("id", idsByType.open_question)
      : Promise.resolve({ data: [] as OpenQuestionRow[], error: null }),
    idsByType.task.length
      ? supabase.from("tasks").select("*").in("id", idsByType.task)
      : Promise.resolve({ data: [] as TaskRow[], error: null }),
    idsByType.feature.length
      ? supabase.from("features").select("*").in("id", idsByType.feature)
      : Promise.resolve({ data: [] as FeatureRow[], error: null }),
    idsByType.pain_point.length
      ? supabase.from("pain_points").select("*").in("id", idsByType.pain_point)
      : Promise.resolve({ data: [] as PainPointRow[], error: null }),
  ]);

  for (const result of [decisions, risks, openQuestions, tasks, features, painPoints]) {
    if (result.error) throw result.error;
  }

  return {
    decisions: decisions.data ?? [],
    risks: risks.data ?? [],
    openQuestions: openQuestions.data ?? [],
    tasks: tasks.data ?? [],
    features: (features.data as FeatureRow[] | null)?.map(normalizeFeatureRow) ?? [],
    painPoints: painPoints.data ?? [],
  };
}

export const meetingsRepository = {
  async list(filters: MeetingFilters = {}): Promise<MeetingRow[]> {
    let q = supabase.from("meetings").select("*").order("meeting_at", { ascending: false });
    if (filters.phaseId) q = q.eq("phase_id", filters.phaseId);
    if (filters.search) q = q.ilike("title", `%${filters.search}%`);
    if (filters.fromDate) q = q.gte("meeting_at", filters.fromDate);
    if (filters.toDate) q = q.lte("meeting_at", filters.toDate);
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
  },

  async byId(id: string): Promise<MeetingDetail | null> {
    const meeting = await supabase.from("meetings").select("*").eq("id", id).maybeSingle();
    if (meeting.error) throw meeting.error;
    if (!meeting.data) return null;
    const [attendees, links] = await Promise.all([
      supabase.from("meeting_attendees").select("*").eq("meeting_id", id),
      supabase.from("meeting_links").select("*").eq("meeting_id", id),
    ]);
    if (attendees.error) throw attendees.error;
    if (links.error) throw links.error;
    return { ...meeting.data, attendees: attendees.data ?? [], links: links.data ?? [] };
  },

  async bySlug(slug: string): Promise<MeetingFullDetail | null> {
    const meeting = await supabase.from("meetings").select("*").eq("slug", slug).maybeSingle();
    if (meeting.error) throw meeting.error;
    if (!meeting.data) return null;

    const meetingId = meeting.data.id;
    const [attendees, links] = await Promise.all([
      supabase.from("meeting_attendees").select("*").eq("meeting_id", meetingId),
      supabase.from("meeting_links").select("*").eq("meeting_id", meetingId),
    ]);
    if (attendees.error) throw attendees.error;
    if (links.error) throw links.error;

    const attendeeStakeholderIds = (attendees.data ?? []).map((row) => row.stakeholder_id);
    const stakeholdersResult = attendeeStakeholderIds.length
      ? await supabase.from("stakeholders").select("*").in("id", attendeeStakeholderIds)
      : { data: [] as StakeholderRow[], error: null };
    if (stakeholdersResult.error) throw stakeholdersResult.error;

    const expanded = await expandLinks(meetingId, links.data ?? []);

    return {
      ...meeting.data,
      attendees: attendees.data ?? [],
      links: links.data ?? [],
      attendeeStakeholders: stakeholdersResult.data ?? [],
      ...expanded,
    };
  },

  async create(input: Omit<MeetingRow, "id" | "created_at" | "updated_at">): Promise<MeetingRow> {
    const { data, error } = await supabase.from("meetings").insert(input).select("*").single();
    if (error) throw error;
    return data;
  },

  async update(id: string, patch: Partial<MeetingRow>): Promise<MeetingRow> {
    const { data, error } = await supabase
      .from("meetings")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from("meetings").delete().eq("id", id);
    if (error) throw error;
  },

  async addAttendee(meetingId: string, stakeholderId: string): Promise<MeetingAttendeeRow> {
    const { data, error } = await supabase
      .from("meeting_attendees")
      .insert({ meeting_id: meetingId, stakeholder_id: stakeholderId })
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async removeAttendee(meetingId: string, stakeholderId: string): Promise<void> {
    const { error } = await supabase
      .from("meeting_attendees")
      .delete()
      .eq("meeting_id", meetingId)
      .eq("stakeholder_id", stakeholderId);
    if (error) throw error;
  },

  async addLink(
    meetingId: string,
    entityType: EntityType,
    entityId: string
  ): Promise<MeetingLinkRow> {
    const { data, error } = await supabase
      .from("meeting_links")
      .insert({ meeting_id: meetingId, entity_type: entityType, entity_id: entityId })
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async removeLink(linkId: string): Promise<void> {
    const { error } = await supabase.from("meeting_links").delete().eq("id", linkId);
    if (error) throw error;
  },

  async meetingsForEntity(entityType: EntityType, entityId: string): Promise<MeetingRow[]> {
    const { data: linkRows, error: linkErr } = await supabase
      .from("meeting_links")
      .select("meeting_id")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId);
    if (linkErr) throw linkErr;
    const ids = (linkRows ?? []).map((row) => row.meeting_id);
    if (ids.length === 0) return [];
    const { data, error } = await supabase
      .from("meetings")
      .select("*")
      .in("id", ids)
      .order("meeting_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  subscribeToChanges(onChange: () => void) {
    const channel = supabase
      .channel("meetings-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "meetings" }, onChange)
      .on("postgres_changes", { event: "*", schema: "public", table: "meeting_links" }, onChange)
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
